import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Switch, message } from 'antd';
import { getAppSettingsApi, updateAppSettingsApi } from '../../services/api';

const { Option } = Select;

const INITIAL = {
  store: {
    name:     'Kidodom',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    language: 'English',
  },
  shipping: {
    freeOver:  '499',
    standard:  '3–5 days',
    express:   '1–2 days',
    returnWin: '7 days',
  },
  notifications: {
    lowStock:    true,
    orderEmails: true,
    reviewAlert: true,
    weeklyReport:true,
  },
  payments: {
    online: true,
    cod:    false,
  },
  mobileTabs: {
    contest:       true,
    parenting:     true,
    subscriptions: true,
  },
  loginOptions: {
    otp: true,
  },
  support: {
    email:      'support@kidodom.com',
    emailNote:  'Response within 24 hours',
    phone:      '+91 800 Kidodom',
    phoneNote:  'Mon–Sat, 9am – 6pm IST',
  },
};

export default function Settings() {
  const [cfg, setCfg]       = useState(INITIAL);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAppSettingsApi()
      .then(({ data }) => {
        const s = data?.data;
        if (!s) return;
        setCfg(prev => ({
          ...prev,
          payments:   { online: s.onlinePaymentEnabled, cod: s.codEnabled },
          mobileTabs: {
            contest:       s.contestTabVisible,
            parenting:     s.parentingTabVisible,
            subscriptions: s.subscriptionsEnabled,
          },
          loginOptions: {
            otp: s.otpLoginEnabled,
          },
          support: {
            email:     s.supportEmail,
            emailNote: s.supportEmailNote,
            phone:     s.supportPhone,
            phoneNote: s.supportPhoneNote,
          },
        }));
      })
      .catch(() => message.error('Failed to load app settings'));
  }, []);

  const set = (section, key, val) =>
    setCfg(prev => ({ ...prev, [section]: { ...prev[section], [key]: val } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAppSettingsApi({
        codEnabled:           cfg.payments.cod,
        onlinePaymentEnabled: cfg.payments.online,
        contestTabVisible:    cfg.mobileTabs.contest,
        parentingTabVisible:  cfg.mobileTabs.parenting,
        subscriptionsEnabled: cfg.mobileTabs.subscriptions,
        otpLoginEnabled:      cfg.loginOptions.otp,
        supportEmail:         cfg.support.email,
        supportEmailNote:     cfg.support.emailNote,
        supportPhone:         cfg.support.phone,
        supportPhoneNote:     cfg.support.phoneNote,
      });
      message.success('Settings saved successfully');
    } catch {
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.05)',
  };
  const labelStyle = { fontSize: 13, color: '#888', minWidth: 160 };
  const card = { background: '#fff', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', padding: 20 };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-sub">Manage your store configuration</div>
        </div>
        <Button
          type="primary"
          loading={saving}
          onClick={handleSave}
          style={{ background: '#534ab7', border: 'none', borderRadius: 8 }}
        >
          Save Changes
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* ── Store Information ── */}
        <div style={card}>
          <div className="section-title">Store Information</div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Store Name</span>
            <Input
              value={cfg.store.name}
              onChange={e => set('store', 'name', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Currency</span>
            <Select value={cfg.store.currency} onChange={v => set('store', 'currency', v)} style={{ width: 200 }}>
              <Option value="INR">INR (₹)</Option>
              <Option value="USD">USD ($)</Option>
              <Option value="EUR">EUR (€)</Option>
              <Option value="GBP">GBP (£)</Option>
            </Select>
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Timezone</span>
            <Select value={cfg.store.timezone} onChange={v => set('store', 'timezone', v)} style={{ width: 200 }}>
              <Option value="Asia/Kolkata">Asia/Kolkata (IST)</Option>
              <Option value="UTC">UTC</Option>
              <Option value="America/New_York">America/New_York</Option>
              <Option value="Europe/London">Europe/London</Option>
            </Select>
          </div>

          <div style={{ ...fieldStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>Language</span>
            <Select value={cfg.store.language} onChange={v => set('store', 'language', v)} style={{ width: 200 }}>
              <Option value="English">English</Option>
              <Option value="Hindi">Hindi</Option>
              <Option value="Tamil">Tamil</Option>
            </Select>
          </div>
        </div>

        {/* ── Shipping & Returns ── */}
        <div style={card}>
          <div className="section-title">Shipping &amp; Returns</div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Free shipping over (₹)</span>
            <Input
              value={cfg.shipping.freeOver}
              onChange={e => set('shipping', 'freeOver', e.target.value)}
              style={{ width: 200 }}
              prefix="₹"
              type="number"
              min={0}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Standard delivery</span>
            <Input
              value={cfg.shipping.standard}
              onChange={e => set('shipping', 'standard', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Express delivery</span>
            <Input
              value={cfg.shipping.express}
              onChange={e => set('shipping', 'express', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={{ ...fieldStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>Return window</span>
            <Input
              value={cfg.shipping.returnWin}
              onChange={e => set('shipping', 'returnWin', e.target.value)}
              style={{ width: 200 }}
            />
          </div>
        </div>

        {/* ── Notifications ── */}
        <div style={card}>
          <div className="section-title">Notifications</div>

          {[
            { key: 'lowStock',    label: 'Low stock alerts' },
            { key: 'orderEmails', label: 'New order emails' },
            { key: 'reviewAlert', label: 'Customer review alerts' },
            { key: 'weeklyReport',label: 'Weekly report' },
          ].map(({ key, label }, i, arr) => (
            <div key={key} style={{ ...fieldStyle, borderBottom: i < arr.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
              <span style={labelStyle}>{label}</span>
              <Switch
                checked={cfg.notifications[key]}
                onChange={v => set('notifications', key, v)}
                checkedChildren="Enabled"
                unCheckedChildren="Disabled"
                style={{ backgroundColor: cfg.notifications[key] ? '#534ab7' : undefined }}
              />
            </div>
          ))}
        </div>

        {/* ── Payment Methods ── */}
        <div style={card}>
          <div className="section-title">Payment Methods</div>
          <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
            Cards, UPI, netbanking, and wallets are one bundled "Online Payment" option in the app — Razorpay's own
            checkout decides which of those to show. Turning this off disables all of them at once.
          </div>

          {[
            { key: 'online', label: 'Online Payment (Cards, UPI, Netbanking, Wallets via Razorpay)' },
            { key: 'cod',    label: 'Cash on Delivery' },
          ].map(({ key, label }, i, arr) => (
            <div key={key} style={{ ...fieldStyle, borderBottom: i < arr.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
              <span style={labelStyle}>{label}</span>
              <Switch
                checked={cfg.payments[key]}
                onChange={v => set('payments', key, v)}
                checkedChildren="Active"
                unCheckedChildren="Disabled"
                style={{ backgroundColor: cfg.payments[key] ? '#534ab7' : undefined }}
              />
            </div>
          ))}
        </div>

        {/* ── Mobile App Tabs ── */}
        <div style={card}>
          <div className="section-title">Mobile App Tabs</div>

          {[
            { key: 'contest',       label: 'Contest Tab' },
            { key: 'parenting',     label: 'Parenting (Guidance) Tab' },
            { key: 'subscriptions', label: 'Subscriptions (Settings)' },
          ].map(({ key, label }, i, arr) => (
            <div key={key} style={{ ...fieldStyle, borderBottom: i < arr.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
              <span style={labelStyle}>{label}</span>
              <Switch
                checked={cfg.mobileTabs[key]}
                onChange={v => set('mobileTabs', key, v)}
                checkedChildren="Visible"
                unCheckedChildren="Hidden"
                style={{ backgroundColor: cfg.mobileTabs[key] ? '#534ab7' : undefined }}
              />
            </div>
          ))}
        </div>

        {/* ── Login Options ── */}
        <div style={card}>
          <div className="section-title">Login Options</div>

          {[
            { key: 'otp', label: 'Login with OTP' },
          ].map(({ key, label }, i, arr) => (
            <div key={key} style={{ ...fieldStyle, borderBottom: i < arr.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
              <span style={labelStyle}>{label}</span>
              <Switch
                checked={cfg.loginOptions[key]}
                onChange={v => set('loginOptions', key, v)}
                checkedChildren="Visible"
                unCheckedChildren="Hidden"
                style={{ backgroundColor: cfg.loginOptions[key] ? '#534ab7' : undefined }}
              />
            </div>
          ))}
        </div>

        {/* ── Contact Support ── */}
        <div style={card}>
          <div className="section-title">Contact Support</div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Support Email</span>
            <Input
              value={cfg.support.email}
              onChange={e => set('support', 'email', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Email Note</span>
            <Input
              value={cfg.support.emailNote}
              onChange={e => set('support', 'emailNote', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Support Phone</span>
            <Input
              value={cfg.support.phone}
              onChange={e => set('support', 'phone', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={{ ...fieldStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>Phone Note</span>
            <Input
              value={cfg.support.phoneNote}
              onChange={e => set('support', 'phoneNote', e.target.value)}
              style={{ width: 200 }}
            />
          </div>
        </div>

      </div>
    </div>
  );
}
